import { useCallback, useMemo } from 'react';
import { ZODIAC_ANIMALS, ZODIAC_SIGNS } from '../utils/constants';
import { ZODIAC_EMOJI, WESTERN_ZODIAC_SYMBOLS } from '../utils/lunarConstants';
import { getLunarSpecialNote } from '../utils/lunarNotes';
import {
  formatDateKey,
  getAuspiciousHoursForDate,
  getDayCanChi,
  getMonthCanChi,
  getZodiacSign,
  solarToLunar
} from '../utils/lunarUtils';

const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

export default function useSelectedDateDetails({
  selectedDate,
  language,
  monthDataMap,
  timezoneOffsetHours,
  zodiacInfoMap,
  t
}) {
  const buildLocalDetails = useCallback((date) => {
    if (!isValidDate(date)) {
      // eslint-disable-next-line no-console
      console.warn('[lunar] Invalid date selected', date);
      return null;
    }

    try {
      const entry = monthDataMap.get(date.toDateString());
      const localLunar = entry?.lunar || solarToLunar(date, timezoneOffsetHours);

      if (!localLunar?.year || !localLunar?.month || !localLunar?.day) {
        // eslint-disable-next-line no-console
        console.warn('[lunar] Invalid lunar data', localLunar);
        return null;
      }

      const animalIndex = ((localLunar.year - 4) % 12 + 12) % 12;
      const animal = ZODIAC_ANIMALS[animalIndex];
      if (!animal) {
        // eslint-disable-next-line no-console
        console.warn('[lunar] Zodiac animal missing', animalIndex);
        return null;
      }

      const zodiacInfo = zodiacInfoMap[animal] || {};

      return {
        solar: {
          date: formatDateKey(date),
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate(),
          formatted: `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
        },
        lunar: {
          ...localLunar,
          formatted: `${localLunar.day}/${localLunar.month}/${localLunar.year}`
        },
        zodiacAnimal: animal,
        auspiciousHours: {
          vi: getAuspiciousHoursForDate(date, 'vi'),
          en: getAuspiciousHoursForDate(date, 'en')
        },
        zodiacInfo: {
          year: localLunar.year,
          animal,
          ...zodiacInfo
        },
        accuracy: 'offline'
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[lunar] Failed to build selected date details', error);
      return null;
    }
  }, [monthDataMap, timezoneOffsetHours, zodiacInfoMap]);

  const selectedDetails = useMemo(
    () => (selectedDate ? buildLocalDetails(selectedDate) : null),
    [buildLocalDetails, selectedDate]
  );

  const precomputedSelection = useMemo(
    () => (isValidDate(selectedDate) ? monthDataMap.get(selectedDate.toDateString()) : null),
    [monthDataMap, selectedDate]
  );

  const computedLunar = useMemo(() => {
    if (precomputedSelection?.lunar) {
      return precomputedSelection.lunar;
    }
    return isValidDate(selectedDate)
      ? solarToLunar(selectedDate, timezoneOffsetHours)
      : null;
  }, [precomputedSelection, selectedDate, timezoneOffsetHours]);

  const selectedLunar = selectedDetails?.lunar || computedLunar;
  const selectedZodiacAnimal = selectedLunar
    ? ZODIAC_ANIMALS[((selectedLunar.year - 4) % 12 + 12) % 12]
    : selectedDetails?.zodiacAnimal;
  const selectedCanChiYear = selectedLunar?.canChiYear;
  const dayCanChi = useMemo(
    () => (isValidDate(selectedDate) ? getDayCanChi(selectedDate) : null),
    [selectedDate]
  );

  const auspiciousHours = useMemo(() => {
    if (selectedDetails?.auspiciousHours) {
      const hours = selectedDetails.auspiciousHours[language];
      if (hours?.good || hours?.bad) {
        return {
          dayBranch: hours.dayBranch,
          good: hours.good || [],
          bad: hours.bad || []
        };
      }
    }

    return isValidDate(selectedDate)
      ? getAuspiciousHoursForDate(selectedDate, language)
      : { good: [], bad: [], dayBranch: null };
  }, [language, selectedDate, selectedDetails?.auspiciousHours]);

  const selectedZodiacSign = isValidDate(selectedDate)
    ? getZodiacSign(selectedDate, ZODIAC_SIGNS)
    : null;
  const selectedZodiacInfo = selectedDetails?.zodiacInfo;
  const localizedDayName = selectedLunar ? (selectedLunar.dayNames?.[language] || selectedLunar.dayName) : null;
  const localizedCanChiYear = language === 'en'
    ? selectedLunar?.canChiYearLocalized?.en
    : selectedLunar?.canChiYear;
  const zodiacDisplay = localizedCanChiYear || selectedZodiacAnimal || selectedDetails?.zodiacAnimal;
  const holidaysForSelectedDate = isValidDate(selectedDate)
    ? (monthDataMap.get(selectedDate.toDateString())?.holidays || [])
    : [];
  const zodiacEmoji = selectedZodiacAnimal ? ZODIAC_EMOJI[selectedZodiacAnimal] : '';
  const westernZodiacSymbol = selectedZodiacSign ? WESTERN_ZODIAC_SYMBOLS[selectedZodiacSign.vi] : '';
  const weekdayLabel = isValidDate(selectedDate)
    ? selectedDate.toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-US', { weekday: 'long' })
    : '';
  const monthCanChi = useMemo(
    () => getMonthCanChi(selectedLunar?.month, selectedLunar?.year),
    [selectedLunar]
  );

  const lunarStemBranchLines = useMemo(() => ([
    { label: t.canChiDayLabel, value: language === 'en' ? dayCanChi?.labelEn : dayCanChi?.label },
    { label: t.canChiMonthLabel, value: language === 'en' ? monthCanChi?.labelEn : monthCanChi?.label },
    { label: t.canChiYearLabel, value: localizedCanChiYear || selectedCanChiYear }
  ].filter(item => item.value)), [
    dayCanChi?.label,
    dayCanChi?.labelEn,
    language,
    localizedCanChiYear,
    monthCanChi?.label,
    monthCanChi?.labelEn,
    selectedCanChiYear,
    t.canChiDayLabel,
    t.canChiMonthLabel,
    t.canChiYearLabel
  ]);

  const formatHourLabel = useCallback((hour) => {
    if (!hour) return '';
    const label = typeof hour === 'string' ? hour : (hour.label || hour.branch || '');
    const start = typeof hour === 'string' ? null : hour.start;
    const end = typeof hour === 'string' ? null : hour.end;
    const timeRange = start && end ? ` (${start}-${end})` : '';
    return `${label}${timeRange}`;
  }, []);

  const lunarSpecialNote = useMemo(
    () => getLunarSpecialNote(selectedLunar, language),
    [language, selectedLunar]
  );

  const selectedNotes = useMemo(() => {
    const notes = [];
    if (selectedDetails?.notes) {
      notes.push(selectedDetails.notes);
    }
    if (lunarSpecialNote) {
      notes.push(lunarSpecialNote);
    }
    return notes;
  }, [lunarSpecialNote, selectedDetails?.notes]);

  return {
    selectedDetails,
    selectedLunar,
    selectedZodiacAnimal,
    selectedCanChiYear,
    dayCanChi,
    auspiciousHours,
    selectedZodiacSign,
    selectedZodiacInfo,
    localizedDayName,
    localizedCanChiYear,
    zodiacDisplay,
    holidaysForSelectedDate,
    zodiacEmoji,
    westernZodiacSymbol,
    weekdayLabel,
    lunarStemBranchLines,
    selectedNotes,
    formatHourLabel
  };
}
